import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { authService, ClientLogoutReason } from '../services/auth/authService';
import { emitApiUiEvent } from '../services/api/apiEvents';
import {
  coordinatedRefreshAccessToken,
  isAccessTokenSyncMessage,
} from './authRefreshCoordinator';
import { usePortalResume } from '../hooks/usePortalResume';

/** Refresh before access JWT expiry (default 10m server-side); avoid skipping refresh when `hasFocus()` is false. */
const SESSION_KEEP_ALIVE_INTERVAL_MS = 7 * 60 * 1000;
/** Resume: token refresh is cheap; short min-hidden, long stale window vs interval refresh. */
const AUTH_RESUME_MIN_HIDDEN_MS = 2_000;
const AUTH_RESUME_STALE_MS = 120_000;

export type AuthContextValue = {
  /** True until initial session bootstrap (CSRF when a token exists) finishes. */
  isLoading: boolean;
  /** Whether a valid access token is present (same source as `authService.isAuthenticated()`). */
  isAuthenticated: boolean;
  /** Clear tokens and bump session so routes re-evaluate. Optional reason is logged server-side. */
  logout: (reason?: string) => Promise<void>;
  /** Call after `authService.storeTokens` in the same tab so context consumers update. */
  notifyAuthChanged: () => void;
};

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [bootstrapped, setBootstrapped] = useState(false);
  const [sessionVersion, setSessionVersion] = useState(0);
  const refreshFailureStreakRef = useRef(0);
  /** Strict Mode runs effects twice; only the latest run should flip `bootstrapped` to avoid stuck loading / wrong auth. */
  const bootstrapSeqRef = useRef(0);

  const notifyAuthChanged = useCallback(() => {
    setSessionVersion((v) => v + 1);
    new BroadcastChannel('auth_sync').postMessage('login');
  }, []);

  const logout = useCallback(async (reason?: string) => {
    await authService.clearTokens({ reason: reason ?? ClientLogoutReason.User });
    setSessionVersion((v) => v + 1);
    new BroadcastChannel('auth_sync').postMessage('logout');
  }, []);

  useEffect(() => {
    const seq = ++bootstrapSeqRef.current;
    let alive = true;
    (async () => {
      try {
        // Since access token is in memory, it will be lost on page reload.
        // We attempt a silent refresh to restore the session using the HttpOnly refresh cookie.
        if (!authService.isAuthenticated()) {
          try {
            await authService.refreshAccessToken();
          } catch {
            // No valid refresh cookie, user is logged out
          }
        }

        if (authService.isAuthenticated()) {
          try {
            await authService.ensureCsrfToken();
          } catch {
            // Session may still be usable; CSRF will be retried on first mutating request
          }
        }
      } finally {
        if (alive && seq === bootstrapSeqRef.current) {
          setBootstrapped(true);
          // Re-read auth into context memo (in-memory token was just set; avoids stale isAuthenticated on first paint after bootstrap)
          if (authService.isAuthenticated()) {
            setSessionVersion((v) => v + 1);
          }
        }
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  // Setup BroadcastChannel to sync auth state across tabs
  useEffect(() => {
    const channel = new BroadcastChannel('auth_sync');
    channel.onmessage = (event) => {
      if (event.data === 'logout') {
        void authService.clearTokens({ remoteLogout: false }).then(() => {
          setSessionVersion((v) => v + 1);
        });
      } else if (event.data === 'login') {
        // Another tab logged in, we should refresh to get the new token
        authService.refreshAccessToken().then(() => {
          setSessionVersion((v) => v + 1);
        }).catch(() => {});
      } else if (isAccessTokenSyncMessage(event.data)) {
        authService.storeTokens(
          event.data.access_token,
          'cookie-managed',
          event.data.csrf_token
        );
        setSessionVersion((v) => v + 1);
      }
    };
    return () => channel.close();
  }, []);

  useEffect(() => {
    const id = setInterval(async () => {
      if (!authService.isAuthenticated()) return;
      // Do not require document.hasFocus(): devtools, second monitor, modals, or long XHR can make focus false while the tab is still the active session.
      if (document.visibilityState !== 'visible') return;
      try {
        const outcome = await coordinatedRefreshAccessToken();
        if (outcome === 'skipped') {
          return;
        }
        refreshFailureStreakRef.current = 0;
      } catch {
        refreshFailureStreakRef.current += 1;
        if (refreshFailureStreakRef.current >= 2) {
          emitApiUiEvent({
            type: 'session_issue',
            message:
              'Session refresh is temporarily unavailable. Please continue working and retry in a moment.',
          });
          refreshFailureStreakRef.current = 0;
        }
      }
    }, SESSION_KEEP_ALIVE_INTERVAL_MS);
    return () => clearInterval(id);
  }, []);

  usePortalResume(
    () => {
      if (!authService.isAuthenticated()) return;
      return coordinatedRefreshAccessToken().then(() => {});
    },
    { minHiddenMs: AUTH_RESUME_MIN_HIDDEN_MS, staleTimeMs: AUTH_RESUME_STALE_MS },
  );

  const isLoading = !bootstrapped;
  const isAuthenticated = authService.isAuthenticated();

  const value = useMemo<AuthContextValue>(
    () => ({
      isLoading,
      isAuthenticated,
      logout,
      notifyAuthChanged,
    }),
    [isLoading, isAuthenticated, logout, notifyAuthChanged, sessionVersion]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return ctx;
}
